import request from '@/utils/request'
import { handleLoginError } from '@/utils/error-handler'

/**
 * 登录请求参数
 * 根据API文档v2.4.0：支持邮箱或手机号登录
 */
export interface LoginRequest {
  identifier: string  // required, string, 邮箱地址或手机号
  password?: string   // optional, string, 密码 (6-128字符) - 首次登录可为空
}

/**
 * 设置密码请求参数
 */
export interface SetPasswordRequest {
  password: string    // required, string, 新密码 (6-128字符)
  confirmPassword: string  // required, string, 确认密码
}

/**
 * 登录响应数据
 * 根据API文档v2.4.0的统一响应格式
 */
export interface LoginResponse {
  success: true
  message: string                    // 登录成功消息
  accessToken: string               // JWT token
  data: {
    id: number
    name: string
    email: string
    role: 'user' | 'admin' | 'super'
    height?: number
    weight?: number
    gender?: string
    passwordHashIsEmpty?: boolean    // 是否需要设置密码
  }
  timestamp: string                 // ISO 8601格式时间戳
}

/**
 * 用户信息接口
 */
export interface CurrentUser {
  id: number
  name: string
  email: string
  phone?: string
  role: 'user' | 'admin' | 'super'
  status: 'active' | 'inactive'
  height?: number
  weight?: number
  created_at: string
  last_login?: string
}

/**
 * API错误响应接口
 * 根据API文档的统一错误格式
 */
export interface ApiErrorResponse {
  success: false
  error: string
  message: string
  timestamp: string
}

/**
 * 登录相关错误代码
 * 根据API文档v2.4.0错误处理规范
 */
export enum LoginErrorCodes {
  // 400 - 参数错误
  INVALID_PARAMS = 'INVALID_PARAMS',                    // 参数格式错误
  MISSING_REQUIRED_FIELDS = 'MISSING_REQUIRED_FIELDS', // 缺少必填字段
  VALIDATION_ERROR = 'VALIDATION_ERROR',               // 数据验证失败
  
  // 401 - 认证失败 (统一消息防止用户名枚举攻击)
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',         // 用户不存在或密码错误
  
  // 403 - 权限不足
  ACCESS_DENIED = 'ACCESS_DENIED',                     // 账号已被禁用
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS', // 权限不足
  
  // 500 - 系统错误
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR'     // 登录服务不可用
}

/**
 * 认证相关 API
 * 根据API文档v2.4.0实现，完整支持新的错误处理体系
 * 
 * 接口列表：
 * - POST /auth/login: 管理员登录认证
 * - GET /auth/me: 获取当前用户信息  
 * - POST /auth/logout: 退出登录
 */
export const AuthApi = {
  /**
   * 管理员登录
   * POST /auth/login
   * 
   * @param payload 登录参数
   * @param payload.identifier 邮箱地址或手机号 (required)
   * @param payload.password 密码，6-128字符 (可选，首次登录时可为空)
   * @returns Promise<LoginResponse> 包含accessToken和用户基本信息
   * 
   * 🔐 认证要求: 无需认证
   * 🔧 需要Header: X-App-Key (生产环境)
   * 
   * ⚠️ 可能的错误:
   * - 400: INVALID_PARAMS, MISSING_REQUIRED_FIELDS, VALIDATION_ERROR
   * - 401: "用户不存在或密码错误" (防止用户名枚举攻击)
   * - 403: "账号已被禁用"
   * - 500: "登录服务暂时不可用"
   */
  async login(payload: LoginRequest): Promise<LoginResponse> {
    try {
      return await request.post('/auth/login', payload) as LoginResponse
    } catch (error) {
      // 不在这里处理错误，让调用方决定如何处理
      throw error
    }
  },

  /**
   * 设置用户密码
   * POST /auth/set-password
   * 
   * @param payload 密码设置参数
   * @param payload.password 新密码，6-128字符 (required)
   * @param payload.confirmPassword 确认密码 (required)
   * @returns Promise<{success: boolean, message: string}>
   * 
   * 🔐 认证要求: 需要有效的JWT token
   * 
   * ⚠️ 可能的错误:
   * - 400: 密码格式错误或两次密码不一致
   * - 401: Token无效或已过期
   * - 500: 系统错误
   */
  async setPassword(payload: SetPasswordRequest): Promise<{success: boolean, message: string}> {
    try {
      return await request.post('/auth/set-password', payload) as {success: boolean, message: string}
    } catch (error) {
      throw error
    }
  },

  /**
   * 获取当前用户信息
   * GET /auth/me
   * 
   * @returns Promise<CurrentUser> 当前用户完整信息
   * 
   * 🔐 认证要求: 需要有效的JWT token
   * 
   * ⚠️ 可能的错误:
   * - 401: Token无效或已过期，需要重新登录
   * - 403: 权限不足
   * - 500: 系统错误
   */
  async getCurrentUser(): Promise<CurrentUser> {
    try {
      return await request.get('/auth/me') as CurrentUser
    } catch (error) {
      throw error
    }
  },

  /**
   * 退出登录
   * @deprecated 不再需要调用后端logout接口，直接删除本地token即可
   * 
   * 💡 注意: 现在的退出登录逻辑是直接清除本地token和用户状态
   */
  async logout(): Promise<void> {
    // 此方法已弃用，退出登录现在只需要清除本地token
    console.warn('logout API已弃用，退出登录现在由前端直接处理')
  },
}
